/**
 * Firebase Dynamic Loading Utility
 * 
 * Provides mechanisms to dynamically load Firebase services only when needed,
 * improving initial page load performance by deferring non-critical Firebase modules.
 */

import type { Analytics } from 'firebase/analytics';
import { getApps, getApp } from 'firebase/app';
import type { Firestore } from 'firebase/firestore';

import { logger } from './logger';

/**
 * Dynamically loads Firebase Analytics and initializes it
 * @returns Promise that resolves with the analytics instance or undefined
 */
export async function initializeFirebaseAnalytics(): Promise<Analytics | null> {
  try {
    // Get the existing Firebase app instance
    const apps = getApps();
    if (apps.length === 0) {
      logger.warn('Cannot initialize Firebase Analytics - Firebase app not initialized');
      return null;
    }
    
    const app = getApp();
    
    // Dynamically import Firebase analytics
    const { getAnalytics, isSupported } = await import(
      /* webpackChunkName: "firebase-analytics" */
      'firebase/analytics'
    );
    
    // Check if analytics is supported in this environment
    const supported = await isSupported();
    if (!supported) {
      logger.warn('Firebase Analytics is not supported in this environment');
      return null;
    }
    
    // Initialize analytics
    const analytics = getAnalytics(app);
    logger.info('Firebase Analytics initialized (deferred loading)');
    return analytics;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to initialize Firebase Analytics', { message, error });
    return null;
  }
}

/**
 * Dynamically loads Firebase Firestore and initializes it
 * @returns Promise that resolves with the firestore instance or undefined
 */
export async function initializeFirebaseFirestore(): Promise<Firestore | null> {
  try {
    // Get the existing Firebase app instance
    const apps = getApps();
    if (apps.length === 0) {
      logger.warn('Cannot initialize Firebase Firestore - Firebase app not initialized');
      return null;
    }
    
    const app = getApp();
    
    // Dynamically import Firebase firestore
    const { getFirestore } = await import(
      /* webpackChunkName: "firebase-firestore" */
      'firebase/firestore'
    );
    
    // Initialize firestore
    const firestore = getFirestore(app);
    logger.info('Firebase Firestore initialized (deferred loading)');
    return firestore;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to initialize Firebase Firestore', { message, error });
    return null;
  }
}

/**
 * Firebase service initialization options
 */
export interface FirebaseInitOptions {
  analyticsDelay?: number;  // Delay before initializing analytics (ms)
  onUserInteraction?: boolean; // Whether to init on first user interaction
}

/**
 * Initialize Firebase services intelligently based on user interaction
 * and page visibility to improve performance
 */
export function setupDeferredFirebaseInit(options: FirebaseInitOptions = {}): () => void {
  const {
    analyticsDelay = 3000,
    onUserInteraction = true
  } = options;
  
  let analyticsInitialized = false;
  let interactionHandler: ((e: Event) => void) | null = null;
  let analyticsTimeout: number | null = null;
  let isCleanedUp = false; // Add flag to prevent operations after cleanup
  
  // Initialize analytics after a delay
  if (analyticsDelay > 0) {
    analyticsTimeout = window.setTimeout(() => {
      if (!analyticsInitialized && !isCleanedUp) {
        analyticsInitialized = true;
        initializeFirebaseAnalytics().catch(error => {
          logger.error('Failed to initialize Firebase Analytics on timeout', { error });
        });
      }
    }, analyticsDelay);
  }
  
  // Initialize analytics on user interaction
  if (onUserInteraction) {
    interactionHandler = (_e: Event) => {
      if (!analyticsInitialized && !isCleanedUp) {
        analyticsInitialized = true;
        
        // Clear timeout if it exists
        if (analyticsTimeout) {
          window.clearTimeout(analyticsTimeout);
          analyticsTimeout = null;
        }
        
        // Initialize analytics with error handling
        initializeFirebaseAnalytics().catch(error => {
          logger.error('Failed to initialize Firebase Analytics on user interaction', { error });
        });
        
        // Remove event listeners
        ['click', 'touchstart', 'scroll'].forEach(eventType => {
          try {
            document.removeEventListener(eventType, interactionHandler as EventListener);
          } catch (error) {
            logger.warn('Error removing event listener', { eventType, error });
          }
        });
      }
    };
    
    // Add event listeners with error handling
    ['click', 'touchstart', 'scroll'].forEach(eventType => {
      try {
        document.addEventListener(eventType, interactionHandler as EventListener);
      } catch (error) {
        logger.warn('Error adding event listener', { eventType, error });
      }
    });
  }
  
  // Return cleanup function
  return () => {
    isCleanedUp = true; // Mark as cleaned up
    
    if (analyticsTimeout) {
      try {
        window.clearTimeout(analyticsTimeout);
      } catch (error) {
        logger.warn('Error clearing analytics timeout', { error });
      }
      analyticsTimeout = null;
    }
    
    if (interactionHandler) {
      ['click', 'touchstart', 'scroll'].forEach(eventType => {
        try {
          document.removeEventListener(eventType, interactionHandler as EventListener);
        } catch (error) {
          logger.warn('Error removing event listener during cleanup', { eventType, error });
        }
      });
      interactionHandler = null;
    }
  };
} 
